import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import CommandPalette from './CommandPalette';
import ImpersonationBanner from './ImpersonationBanner';
import { useMonedaSafe } from '../../contexts/MonedaContext';
import MonedaToggle from '../public/MonedaToggle';
import { ABRIR_SIDEBAR_EVENT } from '../ar-guides/events';

const SIDEBAR_COLLAPSED_KEY = 'mcd_sidebar_collapsed';

/** ¿Es viewport desktop? (md+, 768px). Solo en desktop aplica el colapso del sidebar. */
const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return isDesktop;
};

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false); // mobile off-canvas
  const isDesktop = useIsDesktop();
  // Toggle de moneda (ARS/USD/Const.) a nivel admin: visible solo en rutas de
  // administración y solo si hay MonedaProvider (las rutas admin lo tienen).
  const moneda = useMonedaSafe();
  const { pathname } = useLocation();
  const isAdminRoute =
    pathname.startsWith('/administracion') || pathname === '/operaciones-compra-venta';

  // Colapso del sidebar (riel de íconos) — solo desktop, persistido.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [collapsed]);

  // Guías AR: cuando un paso señala ítems del sidebar y el drawer mobile está
  // cerrado, el overlay lo pide por evento DOM (ver ARGuideOverlay). En desktop
  // el aside está siempre visible y el evento se ignora.
  useEffect(() => {
    const abrirPorGuia = () => {
      if (!isDesktop) setSidebarOpen(true);
    };
    window.addEventListener(ABRIR_SIDEBAR_EVENT, abrirPorGuia);
    return () => window.removeEventListener(ABRIR_SIDEBAR_EVENT, abrirPorGuia);
  }, [isDesktop]);

  // En mobile el sidebar siempre se muestra expandido (drawer off-canvas).
  const sidebarCollapsed = isDesktop && collapsed;

  return (
    // Fondo papel cálido del "ledger"; el chrome (header + sidebar) es forest y recede.
    // En desktop el shell es de alto fijo (md:h-screen): el contenido principal
    // scrollea adentro suyo (main overflow-y-auto) y el sidebar — con su pie de
    // sesión — queda siempre visible en pantalla. En mobile sigue el scroll del
    // documento (el sidebar es un drawer off-canvas).
    <div className="flex min-h-screen flex-col bg-paper md:h-screen md:overflow-hidden">
      {/* Aviso de impersonación (admin de sitio navegando como otro usuario) */}
      <ImpersonationBanner />

      {/* Mobile Header (incluye su propio menú desplegable) */}
      <Header onMobileMenuToggle={sidebarOpen ? () => setSidebarOpen(false) : undefined} />

      <div className="relative flex flex-1 overflow-hidden">
        {/* Overlay móvil */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-40 bg-ink/40 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Sidebar: forest sólido; en desktop colapsable a riel de íconos (w-16). En mobile siempre w-64 off-canvas de alto completo. */}
        <aside
          className={`
            fixed inset-y-0 left-0 z-50 w-64 shrink-0 transform overflow-y-auto overflow-x-hidden mcd-chrome bg-chrome text-white
            transition-all duration-200 ease-in-out
            md:static md:relative md:z-auto md:translate-x-0 ${collapsed ? 'md:w-16' : 'md:w-64'}
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          `}
        >
          <Sidebar
            collapsed={sidebarCollapsed}
            onToggleCollapse={() => setCollapsed((c) => !c)}
            onCloseMobile={sidebarOpen ? () => setSidebarOpen(false) : undefined}
          />
        </aside>

        {/* Contenido principal */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto p-2 sm:p-4 md:p-6 lg:p-8">
          {/* Toggle de moneda — barra sticky arriba, solo admin + argentinos */}
          {moneda && isAdminRoute && moneda.isArgentino && (
            <div className="sticky top-0 z-10 flex flex-wrap items-center justify-end gap-2 mb-2 py-1 bg-paper">
              {moneda.moneda !== 'ARS' && (
                <span className="text-xs text-sage hidden sm:inline">
                  {moneda.moneda === 'USD'
                    ? 'u$ = dólar blue de la fecha de cada operación'
                    : 'c$ = pesos constantes (CER) · ajusta por inflación para comparar valores pasados con los de hoy'}
                </span>
              )}
              <MonedaToggle
                value={moneda.moneda}
                onChange={moneda.setMoneda}
                usdAvailable={moneda.usdAvailable}
                uvaAvailable={moneda.uvaAvailable}
              />
            </div>
          )}
          {children}
        </main>
      </div>

      {/* Command palette (⌘K) — disponible en todo el alcance autenticado */}
      <CommandPalette />
    </div>
  );
};

export default Layout;
